import React, { useEffect, useState } from 'react';
import { Drawer, Space, notification, Switch, Popconfirm, Result } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { XIcon } from '@heroicons/react/outline';
import Table from 'components/Table';
import { useSelector } from 'react-redux';
import './style.css';
import { Breadcrumbs } from '../../components/Atom/breadcumbs';
import { PageHeader } from '../../components/Atom/PageHeader';
import { Button, ResetButton } from '../../components/Atom/Button';
import UserPanel from './userPanel';
import http from '../../utils/http';
import { useQuery } from 'react-query';
import { USER_ROLES } from 'utils/constant';
import { getUsersRole, checkUserPermissions } from 'utils/services';
import SelectStore from 'components/SelectStore';
import { STORE_API_VERSION } from 'utils/constant';
import InstancePanel from './assignGatewayInstance';

const RuleEnabledSwitch = ({ store, linkedUserData, handleState }) => {
  const [statusToggle, setStatusToggle] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  useEffect(() => {
    setStatusToggle(linkedUserData?.enabled);
  }, [linkedUserData]);
  const onUpdate = async (statusToggle) => {
    setIsUpdating(true);
    const payload = {
      userStoreId: linkedUserData?.userStoreId?._id,
      enabled: statusToggle,
      refundAccess: linkedUserData?.refundAccess,
    };
    try {
      const response = await http.put(`/linked/account/${store?._id}`, payload);
      if (response?.data?.success) {
        setStatusToggle(statusToggle);
        notification.success({ message: 'STATUS UPDATE SUCCESSFULLY', placement: 'bottomRight' });
        handleState();
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message ?? err?.message ?? 'SOMETHING WENT WRONG', placement: 'bottomRight' });
    }
    setIsUpdating(false);
  };

  return (
    <React.Fragment>
      <Popconfirm
        title={`Are you sure you want to ${statusToggle ? 'disable' : 'enable'} Link Account Status?`}
        okButtonProps={{ loading: isUpdating }}
        onConfirm={async () => {
          const newStatusToggle = !statusToggle;
          onUpdate(newStatusToggle);
        }}
        okText='Yes'
        cancelText='No'
        placement='topRight'>
        <Switch checked={statusToggle} loading={isUpdating} disabled={isUpdating} name='rule_enabled' />
      </Popconfirm>
    </React.Fragment>
  );
};
const RefundEnabledSwitch = ({ store, linkedUserData, handleState }) => {
  const [statusToggle, setStatusToggle] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  useEffect(() => {
    setStatusToggle(linkedUserData?.refundAccess);
  }, [linkedUserData]);
  const onUpdate = async (statusToggle) => {
    setIsUpdating(true);
    const payload = {
      userStoreId: linkedUserData?.userStoreId?._id,
      enabled: linkedUserData?.enabled,
      refundAccess: statusToggle,
      refundAccessRequest: true,
    };
    try {
      const response = await http.put(`/linked/account/${store?._id}`, payload);
      if (response?.data?.success) {
        setStatusToggle(statusToggle);
        notification.success({ message: 'STATUS UPDATE SUCCESSFULLY', placement: 'bottomRight' });
        handleState();
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message ?? err?.message ?? 'SOMETHING WENT WRONG', placement: 'bottomRight' });
    }
    setIsUpdating(false);
  };

  return (
    <React.Fragment>
      <Popconfirm
        title={`Are you sure you want to ${statusToggle ? 'disable' : 'enable'} refund access?`}
        okButtonProps={{ loading: isUpdating }}
        onConfirm={async () => {
          const newStatusToggle = !statusToggle;
          onUpdate(newStatusToggle);
        }}
        okText='Yes'
        cancelText='No'
        placement='topRight'>
        <Switch checked={statusToggle} loading={isUpdating} disabled={isUpdating} name='rule_enabled' />
      </Popconfirm>
    </React.Fragment>
  );
};
const InstanceEnabledSwitch = ({ store_id, linkedUserData, instanceData, handleState, isDefaultStatusRequest = false }) => {
  const [statusToggle, setStatusToggle] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  useEffect(() => {
    setStatusToggle(isDefaultStatusRequest ? instanceData?.isDefault : instanceData?.enabled);
  }, [instanceData]);
  const onUpdate = async (statusToggle) => {
    setIsUpdating(true);
    const payload = {
      userAccountId: linkedUserData?.userAccountId,
      userStoreId: linkedUserData?.userStoreId?._id,
      enabled: isDefaultStatusRequest ? instanceData?.enabled : statusToggle,
      isDefault: isDefaultStatusRequest ? statusToggle : instanceData?.isDefault,
      instanceId: instanceData?.instanceId,
    };
    try {
      const response = await http.put(`/linked/account/gateway/instance/${store_id}`, payload);
      if (response?.data?.success) {
        setStatusToggle(statusToggle);
        notification.success({ message: 'STATUS UPDATE SUCCESSFULLY', placement: 'bottomRight' });
        handleState();
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message ?? err?.message ?? 'SOMETHING WENT WRONG', placement: 'bottomRight' });
    }
    setIsUpdating(false);
  };

  return (
    <React.Fragment>
      <Popconfirm
        title={`Are you sure you want to ${isDefaultStatusRequest ? 'set default' : statusToggle ? 'disable' : 'enable'} instance status?`}
        okButtonProps={{ loading: isUpdating }}
        disabled={(isDefaultStatusRequest && instanceData?.isDefault === true) || isUpdating}
        onConfirm={async () => {
          const newStatusToggle = !statusToggle;
          onUpdate(newStatusToggle);
        }}
        okText='Yes'
        cancelText='No'
        placement='topRight'>
        <Switch checked={statusToggle} loading={isUpdating} disabled={isUpdating} name='status_enabled' />
      </Popconfirm>
    </React.Fragment>
  );
};
const LinkedAccount = () => {
  const [store, setStore] = useState(useSelector((state) => state.company.store));
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null);
  const { isMerchantAccount, isPlatformAccount } = getUsersRole();
  const [linkedUserData, setLinkedUserData] = useState(null);
  const handleOpen = (type = 'addRequest', linkedUserData = null) => {
    setOpen(!open);
    setDrawerType(type);
    setLinkedUserData(linkedUserData);
  };

  const getLinkedAccountData = async () => {
    try {
      const res = await http.get(`/linked/account/all/data/${store?._id}`);
      return res?.data?.data;
    } catch (err) {
      console.log(err);
    }
  };
  const { data, refetch, isLoading, isRefetching } = useQuery('linkedAccounts', getLinkedAccountData, {
    enabled: false,
    manual: true,
  });
  useEffect(() => {
    refetch();
  }, [store]);
  const handleDeleteLinkAccount = async (value) => {
    try {
      setLoading(true);
      const response = await http.delete(`/linked/account/${store?._id}/${value?.userStoreId?._id}`);
      if (response?.data?.success) {
        notification.success({ message: 'ACCOUNT UNLINKED SUCCESSFULLY', placement: 'bottomRight' });
        refetch();
      } else {
        notification.error({ message: 'SOMETHING WENT WRONG', placement: 'bottomRight' });
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message ?? err?.message ?? 'SOMETHING WENT WRONG', placement: 'bottomRight' });
    }
    setLoading(false);
  };
  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'Home',
        href: '/xpay',
      },
      {
        name: 'Linked Accounts',
      },
    ],
  };
  const columnsData = [
    {
      title: '#',
      render: (text, record, index) => index + 1,
      key: 'index',
    },
    {
      title: 'Account Id',
      render: (value) => value?.userAccountId,
      key: 'account_id',
    },
    {
      title: 'Store Id',
      render: (value) => value?.userStoreId?._id ?? '-',
      key: 'account_id',
    },
    {
      title: 'Store Name',
      render: (value) => value?.userStoreId?.name ?? '-',
      key: 'username',
    },
    {
      title: 'Request Status',
      render: (value) => (value.requestStatus ? 'Approved' : 'Pending'),
      key: 'request_status',
    },
    ...(isPlatformAccount
      ? [
          {
            title: 'Refund Access',
            render: (value) => (value?.refundAccess ? 'Enabled' : 'Disabled'),
            key: 'refund_access',
          },
        ]
      : []),
    ...(checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN])
      ? [
          {
            title: 'Delete',
            key: 'action',
            render: (data, index) => {
              return (
                <div key={index} className='flex items-center'>
                  <Popconfirm
                    title={`Are you sure you want to delete ${data?.requestStatus ? 'linked' : 'link'} account ${data?.requestStatus ? '' : 'request'}?`}
                    okButtonProps={{ loading: loading }}
                    onConfirm={async () => {
                      handleDeleteLinkAccount(data);
                    }}
                    okText='Yes'
                    cancelText='No'
                    placement='topRight'>
                    <a style={{ color: 'red' }}>
                      <DeleteOutlined
                        className='text-[22px]'
                        style={{
                          color: 'red',
                        }}
                      />
                    </a>
                  </Popconfirm>
                </div>
              );
            },
          },
          {
            title: 'Enabled',
            render: (value) => <RuleEnabledSwitch store={store} linkedUserData={value} handleState={refetch} />,
            key: 'rule_enabled',
          },
          ...(isMerchantAccount
            ? [
                {
                  title: 'Refund Access',
                  render: (value) => <RefundEnabledSwitch store={store} linkedUserData={value} handleState={refetch} />,
                  key: 'rule_enabled',
                },
              ]
            : []),
        ]
      : []),
  ];
  const handleOnStoreChange = (e) => {
    setStore(e);
  };
  const deleteLinkedInstance = async (linkedUserData, instanceId) => {
    try {
      const params = {
        userAccountId: linkedUserData?.userAccountId,
        userStoreId: linkedUserData?.userStoreId?._id,
        instanceId,
      };
      const response = await http.delete(`/linked/account/gateway/instance/${store?._id}`, { params });
      if (response?.data?.success) {
        refetch();
        notification.success({ message: 'INSTANCE DELETED SUCCESSFULLY', placement: 'bottomRight' });
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message ?? err?.message ?? 'SOMETHING WENT WRONG', placement: 'bottomRight' });
    }
  };
  const rowExpandable = (record) => {
    if (store?.apiVersion !== STORE_API_VERSION.OCTOBER_23) return false;
    if (isMerchantAccount) return true;
    return record?.assignedGatewayInstance?.length > 0 ? true : false;
  };
  const expandedRowRender = (record) => {
    const InstanceColumnsData = [
      {
        title: 'Name',
        render: (value) => value?.instanceName,
        key: 'name',
      },
      {
        title: 'Id',
        render: (value) => value?.instanceId,
        key: 'id',
      },
      ...(isPlatformAccount
        ? [
            {
              title: 'Status',
              render: (value) => (value?.enabled ? 'Enabled' : 'Disabled'),
              key: 'status',
            },
          ]
        : []),
      ...(checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN]) && isMerchantAccount
        ? [
            {
              title: 'Delete',
              key: 'action',
              render: (data, index) => {
                return (
                  <div key={index} className='flex items-center'>
                    <Popconfirm
                      title={`Are you sure you want to delete linked instance ?`}
                      okButtonProps={{ loading: loading }}
                      onConfirm={async () => {
                        deleteLinkedInstance(record, data?.instanceId);
                      }}
                      okText='Yes'
                      cancelText='No'
                      placement='topRight'>
                      <a style={{ color: 'red' }}>
                        <DeleteOutlined
                          className='text-[22px]'
                          style={{
                            color: 'red',
                          }}
                        />
                      </a>
                    </Popconfirm>
                  </div>
                );
              },
            },
            {
              title: 'Status',
              render: (value) => <InstanceEnabledSwitch store_id={store?._id} instanceData={value} linkedUserData={record} handleState={refetch} />,
              key: 'instance_status',
            },
          ]
        : []),
      ...(checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN])
        ? [
            {
              title: 'Default',
              render: (value) => <InstanceEnabledSwitch store_id={store?._id} instanceData={value} linkedUserData={record} handleState={refetch} isDefaultStatusRequest={true} />,
              key: 'default_instance',
            },
          ]
        : []),
    ];
    return (
      <div style={{ padding: '0px', width: '100%', backgroundColor: 'white', border: '1px solid #F0F0F0' }}>
        <div className='headerPage' style={{ padding: '10px 5px' }}>
          <p className='instance-title'>Assigned Gateway Instance</p>
          {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN]) && isMerchantAccount && (
            <Button
              title={record?.requestStatus ? '' : 'Please approve the account link request first before any operation'}
              disabled={!record?.requestStatus}
              onClick={() => handleOpen('assignInstance', record)}>
              Add Instance
            </Button>
          )}
        </div>
        <Table
          locale={
            !record?.requestStatus
              ? {
                  emptyText: <Result subTitle={`The account link request has not been approved yet.${isMerchantAccount ? ' Please approve it before proceeding with any operations.' : ''}`} />,
                }
              : null
          }
          columns={InstanceColumnsData}
          dataSource={record?.requestStatus ? record?.assignedGatewayInstance ?? [] : []}
          pagingation={{ show: false }}
        />
      </div>
    );
  };
  return (
    <div style={{ padding: '0px', width: '100%' }}>
      <div style={{ marginTop: '10px' }}>
        <Breadcrumbs {...breadcrumbProps} />
      </div>
      <div className='headerPage'>
        <div>
          <PageHeader label='Linked Accounts' button={<ResetButton onClick={isRefetching ? '' : refetch} loading={isRefetching} />} />
        </div>
        <div className='flexStoreFields m-r-10'>
          <SelectStore onChange={handleOnStoreChange} setDefault={true} />
          {isPlatformAccount && checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN]) && (
            <Button
              title={store?.apiVersion === STORE_API_VERSION.OCTOBER_23 ? '' : 'The current store API does not support this feature. Please upgrade to the latest API version.'}
              disabled={store?.apiVersion === STORE_API_VERSION.OCTOBER_23 ? false : true}
              onClick={() => handleOpen()}>
              Add Request
            </Button>
          )}
        </div>
      </div>
      {/* transection detail view */}
      <Drawer
        title={drawerType === 'addRequest' ? 'Add Link Account' : 'Assign Gateway Instance'}
        destroyOnClose
        onClose={handleOpen}
        closable={false}
        visible={open}
        className={'SessiondetailsPanel'}
        extra={
          <Space>
            <div className='ml-3 h-7 flex items-center'>
              <button className='bg-red rounded-circle text-gray-400 hover:text-gray-500 focus:ring-indigo-500' onClick={handleOpen}>
                <XIcon className='h-6 w-6' aria-hidden='true' />
              </button>
            </div>
          </Space>
        }>
        {drawerType === 'addRequest' ? (
          <UserPanel handleOpen={handleOpen} updateState={refetch} store={store} />
        ) : (
          <InstancePanel handleOpen={handleOpen} updateState={refetch} store={store} linkedUserData={linkedUserData} />
        )}
      </Drawer>
      <br />
      <div className='overflow-x-auto'>
        <Table
          expandable={{ expandedRowRender, rowExpandable }}
          rowKey={(e) => e?.userStoreId?._id}
          loading={isLoading}
          dataSource={data?.linkedAccounts}
          columns={columnsData}
          totalLenght={null}
          pagingation={{ show: false }}
        />
      </div>
    </div>
  );
};

export default LinkedAccount;
